import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';

import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { parseTableParams } from '../../../utils/Utils';
import DataTable from '../../../components/ui/DataTable';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import PersonCell from '../../../components/ui/PersonCell';
import AssignmentTargetPicker, { EMPTY_TARGETS } from '../../../components/ui/AssignmentTargetPicker';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $s }) =>
    $s === 'active' ? '#DCFCE7' :
    $s === 'pending' ? '#FEF3C7' :
    $s === 'suspended' ? '#E0E7FF' :
    $s === 'terminated' ? '#FEE2E2' : '#E0E7FF'};
  color: ${({ $s }) =>
    $s === 'active' ? '#166534' :
    $s === 'pending' ? '#92400E' :
    $s === 'suspended' ? '#3730A3' :
    $s === 'terminated' ? '#991B1B' : '#3730A3'};
`;

const DrawerHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; border-bottom: 1px solid #E8ECF0;
`;
const DrawerTitle = styled.h3`
  font-size: 16px; font-weight: 700; color: #004242;
  font-family: 'Nunito Sans', sans-serif;
  margin: 0;
`;
const CloseBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border: none; border-radius: 4px;
  background: none; color: #A0A8B4; cursor: pointer;
  &:hover { background: #F5F7FA; }
`;
const DrawerBody = styled.div`
  flex: 1; overflow-y: auto; padding: 24px;
  display: flex; flex-direction: column; gap: 16px;
`;
const DrawerFooter = styled.div`
  display: flex; justify-content: flex-end; gap: 12px;
  padding: 16px 24px; border-top: 1px solid #E8ECF0;
`;

const emptyTargets = () => ({
  ...EMPTY_TARGETS,
  excludes: { ...EMPTY_TARGETS.excludes },
});

export default function AssignmentsTab({ packageId, onAssignmentsChanged }) {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 20 });
  const [fetching, setFetching] = useState(true);
  const [queryParams, setQueryParams] = useState({ pageNumber: 1, pageSize: 20 });
  const [filterList, setFilterList] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [targets, setTargets] = useState(emptyTargets());

  const [unassignConfirm, setUnassignConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [unassigning, setUnassigning] = useState(false);

  const fetchAssignments = useCallback(() => {
    setFetching(true);
    const params = new URLSearchParams(queryParams);
    api.get(`/v1/benefit-packages/${packageId}/assignments?${params}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 20,
        });
      })
      .catch(() => setData([]))
      .finally(() => setFetching(false));
  }, [packageId, queryParams]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  const statusLabels = useMemo(() => ({
    active: t('employeeBenefit.statusActive'),
    pending: t('employeeBenefit.statusPending'),
    suspended: t('employeeBenefit.statusSuspended'),
    terminated: t('employeeBenefit.statusTerminated'),
  }), [t]);

  const baseColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeFullName',
      label: t('employeeBenefit.employee'),
      options: {
        filter: false, sort: true,
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <PersonCell name={value || '-'} profilePicture={row?.employeeProfilePicture} />;
        },
      },
    },
    { name: 'benefitName', label: t('employeeBenefit.benefit'), options: { filter: false, sort: true } },
    { name: 'categoryName', label: t('employeeBenefit.category'), options: { filter: false, sort: false } },
    {
      name: 'status', label: t('employeeBenefit.status'),
      options: {
        filter: true, sort: true, filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['active', 'pending', 'suspended', 'terminated'],
          renderValue: (v) => statusLabels[v] || v,
        },
        customFilterListOptions: { render: (v) => statusLabels[v] || v },
        customBodyRender: (v) => <StatusBadge $s={v}>{statusLabels[v] || v}</StatusBadge>,
      },
    },
    {
      name: 'activationDate', label: t('employeeBenefit.activationDate'),
      options: { filter: false, sort: true,
        customBodyRender: (v) => v ? new Date(v).toLocaleDateString('tr-TR') : '-' },
    },
    {
      name: 'monthlyCostSnapshot', label: t('employeeBenefit.monthlyCostSnapshot'),
      options: { filter: false, sort: true,
        customBodyRender: (v) => `${Number(v ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺` },
    },
  ], [t, data, statusLabels]);

  const actionOptions = useMemo(() => {
    if (!canWrite('BN')) return [];
    return [
      {
        type: 'delete',
        label: t('benefitPackage.unassign'),
        onClick: (row) => setUnassignConfirm({
          isOpen: true,
          id: row.id,
          name: `${row.employeeFullName} — ${row.benefitName}`,
        }),
      },
    ];
  }, [t, canWrite]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newList) => setFilterList(newList), []);

  const hasAnyTarget = targets.includeAll
    || targets.userIds.length > 0
    || targets.departmentIds.length > 0
    || targets.locationIds.length > 0
    || targets.userGroupIds.length > 0
    || targets.subTenantIds.length > 0
    || (targets.titleIds && targets.titleIds.length > 0);

  const handleOpenDrawer = useCallback(() => {
    setTargets(emptyTargets());
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!hasAnyTarget) return;
    setSaving(true);
    try {
      await api.post('/v1/benefit-packages/assignments', {
        benefitPackageId: packageId,
        ...targets,
      });
      setDrawerOpen(false);
      fetchAssignments();
      if (onAssignmentsChanged) onAssignmentsChanged();
    } catch {
      // interceptor
    } finally {
      setSaving(false);
    }
  }, [hasAnyTarget, packageId, targets, fetchAssignments, onAssignmentsChanged]);

  const handleUnassignConfirm = useCallback(async () => {
    if (!unassignConfirm.id) return;
    setUnassigning(true);
    try {
      await api.delete(`/v1/employee-benefits/${unassignConfirm.id}`);
      setUnassignConfirm({ isOpen: false, id: null, name: '' });
      fetchAssignments();
      if (onAssignmentsChanged) onAssignmentsChanged();
    } catch {
      // interceptor
    } finally {
      setUnassigning(false);
    }
  }, [unassignConfirm.id, fetchAssignments, onAssignmentsChanged]);

  return (
    <div>
      <DataTable
        title={t('benefitPackage.assignments')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canWrite('BN')}
        newButtonLabel={t('benefitPackage.newAssignment')}
        onNewButtonClick={handleOpenDrawer}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
      />

      <ConfirmDialog
        isOpen={unassignConfirm.isOpen}
        title={t('benefitPackage.unassignTitle')}
        message={t('benefitPackage.unassignMessage', { name: unassignConfirm.name })}
        onConfirm={handleUnassignConfirm}
        onCancel={() => setUnassignConfirm({ isOpen: false, id: null, name: '' })}
        loading={unassigning}
      />

      <Drawer
        open={drawerOpen}
        onClose={() => !saving && setDrawerOpen(false)}
        direction="right"
        size="45%"
        duration={150}
        overlayOpacity={0.2}
        style={{
          display: 'flex', flexDirection: 'column', maxWidth: '100vw', minWidth: 380,
          margin: '12px', borderRadius: '6px', height: 'calc(100vh - 24px)',
          boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)', zIndex: 1000,
        }}
      >
        <DrawerHeader>
          <DrawerTitle>{t('benefitPackage.newAssignment')}</DrawerTitle>
          <CloseBtn onClick={() => setDrawerOpen(false)}><X size={20} /></CloseBtn>
        </DrawerHeader>
        <DrawerBody>
          <AssignmentTargetPicker value={targets} onChange={setTargets} showAutoApply />
        </DrawerBody>
        <DrawerFooter>
          <Button variant="outline" onClick={() => setDrawerOpen(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasAnyTarget}>
            {saving ? '...' : t('common.save')}
          </Button>
        </DrawerFooter>
      </Drawer>
    </div>
  );
}
